import { FormContainer, FormInput, useFetch } from "@4uhub/lib4uhub";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getDashboardGenerateConfig,
  updateDashboardGenerateConfig,
} from "../../../../services/dashboardGenerateExecution.service";
import {
  TGenerateConfigForm,
  generateConfigSchema,
} from "./GenerateConfigSchema";

const translationPath = "page.dashboardGenerateConfig.";

const GenerateConfigPage = () => {
  const { t } = useTranslation();

  const [configId, setConfigId] = useState<string>();

  const { sendRequest: get, loading: gLoading } = useFetch(
    getDashboardGenerateConfig,
  );
  const { sendRequest: update, loading: uLoading } = useFetch(
    updateDashboardGenerateConfig,
  );

  useEffect(() => {
    get(undefined).then(({ data, success }) => {
      if (success && data) setConfigId(data.id);
    });
  }, [get]);

  const getHandler = useCallback(() => get(undefined), [get]);

  const updateHandler = (v: TGenerateConfigForm, id: string) =>
    update({
      item: { ...v, id, alertEmails: v.alertEmails?.trim() || null },
      id,
    });

  if (!configId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const numberField = (
    name: keyof TGenerateConfigForm,
    helper?: React.ReactNode,
  ) => (
    <Grid item xs={12} sm={6}>
      <FormInput
        size="small"
        type="number"
        name={name}
        fullWidth
        label={t(translationPath + name)}
      />
      {helper && (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      )}
    </Grid>
  );

  return (
    <FormContainer<TGenerateConfigForm>
      isModalForm
      formActionsOnBottom={false}
      propId={configId}
      getFunction={getHandler}
      updateFunction={updateHandler}
      loading={gLoading || uLoading}
      title={t(translationPath + "title")}
      subtitle={t(translationPath + "subtitle")}
      schema={generateConfigSchema}
      showBackButton={false}
      redirectOnSave={false}
      fields={() => (
        <Grid container spacing={2}>
          {numberField(
            "maxBatchSize",
            t(translationPath + "max_batch_size_helper"),
          )}
          {numberField(
            "maxBatchNumbers",
            t(translationPath + "max_batch_numbers_helper"),
          )}
          {numberField("maxRetries", t(translationPath + "max_retries_helper"))}
          {numberField(
            "maxPendingBatchTransactions",
            t(translationPath + "max_pending_batch_transactions_helper"),
          )}
          {numberField(
            "retentionDaysReduced",
            t(translationPath + "retention_days_reduced_helper"),
          )}
          {numberField(
            "retentionDaysFailed",
            t(translationPath + "retention_days_failed_helper"),
          )}
          <Grid item xs={12}>
            <FormInput
              size="small"
              name="alertEmails"
              fullWidth
              multiline
              minRows={2}
              label={t(translationPath + "alertEmails")}
            />
            <Typography variant="caption" color="text.secondary">
              {t(translationPath + "alert_emails_helper")}
            </Typography>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default GenerateConfigPage;
